import { type ReactNode, useState } from 'react';
import { ActionIcon, Box, Kbd, Loader, Text, UnstyledButton } from '@mantine/core';
import { Spotlight, type SpotlightActionData, type SpotlightProps } from '@mantine/spotlight';
import {
  IconBuilding,
  IconCheckupList,
  IconFileDollar,
  IconHome,
  IconPlus,
  IconSearch,
  IconSettings,
  IconShield,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';

import { canApprove } from '@/auth/roleAccess';
import { accountStatusBadge } from '@/components/accounts/list/accountStatus';
import { getVisibleAdminRoutes } from '@/components/layout/nav/adminRoutes';
import { desktopNavHref } from '@/components/layout/nav/primaryRoutes';
import { quoteStatusBadge, TYPE_LABELS } from '@/components/quotes/list/quoteStatus';
import { formatQuoteHeadline } from '@/components/quotes/list/quoteHeadline';
import { StatusBadge } from '@/components/ri/StatusBadge';
import { useAuth } from '@/hooks/useAuth';
import { useGlobalSearch } from '@/hooks/useGlobalSearch';
import { formatDollars } from '@/lib/format';

import './GlobalSearch.css';

interface ActionSpec {
  id: string;
  label: string;
  description: string;
  icon: ReactNode;
  path: string;
  rightSection?: ReactNode;
}

const ICON_SIZE = 18;

function toAction(spec: ActionSpec, navigate: (path: string) => void): SpotlightActionData {
  return {
    id: spec.id,
    label: spec.label,
    description: spec.description,
    leftSection: spec.icon,
    rightSection: spec.rightSection,
    onClick: () => navigate(spec.path),
  };
}

export function GlobalSearch() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [query, setQuery] = useState('');
  const approvalAccess = canApprove(user?.role);
  const adminRoutes = getVisibleAdminRoutes(user);
  const { accounts, quotes, approvals, isFetching, isError } = useGlobalSearch(query, approvalAccess);

  const navigationSpecs: ActionSpec[] = [
    { id: 'new-quote', label: 'Create a new quote', description: 'Start a live quoting workflow', path: '/quotes/new', icon: <IconPlus size={ICON_SIZE} /> },
    { id: 'dashboard', label: 'Dashboard', description: 'Your current sales command center', path: desktopNavHref('home'), icon: <IconHome size={ICON_SIZE} /> },
    { id: 'accounts', label: 'Accounts', description: 'Browse and filter customer accounts', path: desktopNavHref('accounts'), icon: <IconBuilding size={ICON_SIZE} /> },
    { id: 'quotes', label: 'Quotes', description: 'Open the quote pipeline', path: desktopNavHref('quotes'), icon: <IconFileDollar size={ICON_SIZE} /> },
    { id: 'settings', label: 'Settings', description: 'Profile, notifications, and appearance', path: '/settings', icon: <IconSettings size={ICON_SIZE} /> },
    ...(approvalAccess
      ? [{ id: 'approvals', label: 'Approvals', description: 'Review requests waiting for a decision', path: desktopNavHref('approvals'), icon: <IconCheckupList size={ICON_SIZE} /> }]
      : []),
    ...(adminRoutes.length > 0
      ? [{ id: 'administration', label: 'Administration', description: 'Users, rules, catalog, and integrations', path: desktopNavHref('admin'), icon: <IconShield size={ICON_SIZE} /> }]
      : []),
  ];

  const resultGroups: SpotlightProps['actions'] = [];

  if (accounts.length) {
    resultGroups.push({
      group: 'Accounts',
      actions: accounts.map((account) => {
        const badge = accountStatusBadge(account.status);
        return toAction(
          {
            id: `account-${account.id}`,
            label: account.name,
            description: [account.ownerName ?? account.ownerEmail, account.industry].filter(Boolean).join(' · ') || badge.label,
            icon: <IconBuilding size={ICON_SIZE} />,
            rightSection: <StatusBadge value={badge.value}>{badge.label}</StatusBadge>,
            path: `/accounts/${account.id}`,
          },
          navigate,
        );
      }),
    });
  }

  if (quotes.length) {
    resultGroups.push({
      group: 'Quotes',
      actions: quotes.map((quote) => {
        const badge = quoteStatusBadge(quote.status, quote.changesRequested);
        return toAction(
          {
            id: `quote-${quote.id}`,
            label: `${quote.quoteNumber} · ${quote.accountName}`,
            description: `${TYPE_LABELS[quote.quoteType] ?? quote.quoteType} · ${formatQuoteHeadline(quote)}`,
            icon: <IconFileDollar size={ICON_SIZE} />,
            rightSection: <StatusBadge value={badge.value}>{badge.label}</StatusBadge>,
            path: `/quotes/${quote.id}`,
          },
          navigate,
        );
      }),
    });
  }

  if (approvals.length) {
    resultGroups.push({
      group: 'Approvals',
      actions: approvals.map((approval) =>
        toAction(
          {
            id: `approval-${approval.id}`,
            label: `${approval.quoteNumber} · ${approval.accountName}`,
            description: `${approval.requestedByName} · ${formatDollars(approval.quoteTotal)}`,
            icon: <IconCheckupList size={ICON_SIZE} />,
            rightSection: <StatusBadge value={approval.status.toLowerCase()} />,
            path: `/approvals?focus=${approval.id}`,
          },
          navigate,
        ),
      ),
    });
  }

  const actions: SpotlightProps['actions'] = [
    ...resultGroups,
    {
      group: query.trim().length >= 2 ? 'Go to' : 'Quick actions',
      actions: navigationSpecs.map((spec) => toAction(spec, navigate)),
    },
  ];

  return (
    <Spotlight
      classNames={{ content: 'ri-spotlight' }}
      actions={isError ? [] : actions}
      filter={(_, unfiltered) => unfiltered}
      query={query}
      onQueryChange={setQuery}
      shortcut="mod + K"
      limit={18}
      highlightQuery
      scrollable
      maxHeight="min(560px, 70vh)"
      searchProps={{
        leftSection: <IconSearch size={ICON_SIZE} />,
        rightSection: isFetching ? <Loader size="xs" color="var(--accent)" /> : undefined,
        placeholder: 'Search accounts, quotes, approvals, or pages…',
        'aria-label': 'Search Meridian',
      }}
      nothingFound={(
        <Text size="sm" c={isError ? 'red' : 'dimmed'} ta="center" py="lg">
          {isError ? 'Search is temporarily unavailable. Try again.' : 'No matching accounts, quotes, approvals, or pages.'}
        </Text>
      )}
    />
  );
}

function isApplePlatform(): boolean {
  if (typeof navigator === 'undefined') return false;
  const platform =
    (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform ??
    navigator.platform;
  return /mac|iphone|ipad|ipod/i.test(platform ?? '');
}

export function GlobalSearchTrigger() {
  return (
    <>
      <Box visibleFrom="md">
        <UnstyledButton
          onClick={Spotlight.open}
          aria-label="Search Meridian"
          style={{
            alignItems: 'center',
            background: 'var(--surface-sunken)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--r-sm)',
            color: 'var(--text-secondary)',
            display: 'grid',
            fontSize: 12.5,
            gap: 8,
            gridTemplateColumns: 'auto 1fr auto',
            height: 34,
            padding: '0 8px 0 10px',
            textAlign: 'left',
            width: 260,
          }}
        >
          <IconSearch size={15} />
          <span>Search Meridian…</span>
          <Kbd size="xs">{isApplePlatform() ? '⌘ K' : 'Ctrl K'}</Kbd>
        </UnstyledButton>
      </Box>
      <Box hiddenFrom="md">
        <ActionIcon variant="subtle" size="lg" color="gray" onClick={Spotlight.open} aria-label="Search Meridian">
          <IconSearch size={ICON_SIZE} />
        </ActionIcon>
      </Box>
    </>
  );
}
